import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollStatus(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async (attempts) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await api.getStripeStatus(sessionId);
      setData(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Keep polling
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch (error) {
      console.error('Status check failed:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
              <p className="text-gray-500">Please wait while we verify your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-green-700 mb-2">Payment Successful!</h2>
              <p className="text-gray-600 mb-6">
                Your rent payment of {data?.amount_total ? (data.amount_total / 100).toLocaleString() : '—'} {data?.currency?.toUpperCase() || 'SAR'} has been processed.
              </p>
              <Button onClick={() => navigate('/payments')} className="w-full">
                Back to Payments
              </Button>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-red-700 mb-2">
                {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === 'expired'
                  ? 'The payment session has expired. Please try again.'
                  : 'There was an issue verifying your payment. Please check your payment history.'}
              </p>
              <Button onClick={() => navigate('/payments')} className="w-full">
                Back to Payments
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
